/**
 * The installable app: service worker registration, the install prompt, and push notifications
 * on this device. Push needs a secure page (https or localhost) and, on iPhone, the app added to
 * the home screen first.
 */
import { pushAPI } from './api/crm'

let installEvent = null
const listeners = new Set()

export const registerServiceWorker = () => {
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* no offline / push on this browser */ })
  })
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    installEvent = e
    listeners.forEach((l) => l())
  })
  window.addEventListener('appinstalled', () => {
    installEvent = null
    listeners.forEach((l) => l())
  })
}

/** Chrome / Edge / Android offer their own install prompt; iPhone needs Share → Add to Home Screen. */
export const installState = () => {
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent)
  return { installed: standalone, canPrompt: !!installEvent, ios }
}
export const onInstallChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn) }
export const promptInstall = async () => {
  if (!installEvent) return false
  installEvent.prompt()
  const choice = await installEvent.userChoice
  installEvent = null
  listeners.forEach((l) => l())
  return choice.outcome === 'accepted'
}

export const pushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window && window.isSecureContext

const keyBytes = (b64url) => {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4)
  const raw = atob((b64url + pad).replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(raw, (c) => c.charCodeAt(0))
}

/** Whether this device currently gets pushes. */
export const pushEnabledHere = async () => {
  if (!pushSupported()) return false
  const reg = await navigator.serviceWorker.getRegistration()
  return !!(await reg?.pushManager.getSubscription()) && Notification.permission === 'granted'
}

/** Asks permission, subscribes this device and tells the server. Throws a readable Error. */
export const enablePush = async () => {
  if (!pushSupported()) throw new Error('This browser can’t receive notifications. On iPhone, add the app to the home screen first.')
  const config = await pushAPI.config()
  if (!config.enabled) throw new Error('Push notifications are off on this installation.')
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new Error('Notifications are blocked for this site. Allow them in the browser’s site settings.')
  const reg = await navigator.serviceWorker.ready
  let sub = await reg.pushManager.getSubscription()
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(config.publicKey) })
  await pushAPI.subscribe(sub.toJSON())
}

export const disablePush = async () => {
  const reg = await navigator.serviceWorker.getRegistration()
  const sub = await reg?.pushManager.getSubscription()
  if (sub) {
    await pushAPI.unsubscribe(sub.endpoint).catch(() => {})
    await sub.unsubscribe()
  }
}
